import { hitTestNode } from '../../provider/interactive';
import { EventTypes, SHAPES, TYPES } from '../../render/constants';
import { resolveRadius } from '../../render/utils/radius';
import { Rect } from '../region';

// Visual style for the radius handler: small blue dot inside corner
function drawBlueDot(context, center, size, isActive) {
  const radius = size / 2;
  context.save();
  context.fillStyle = isActive ? '#8b3eff' : '#fff'; // active: deeper blue
  context.strokeStyle = '#8b3eff';
  context.lineWidth = 1;

  // subtle shadow
  context.shadowColor = 'rgba(0,0,0,0.18)';
  context.shadowBlur = 6;
  context.shadowOffsetY = 2;

  context.beginPath();
  context.arc(center.x, center.y, radius, 0, Math.PI * 2);
  context.fill();
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.beginPath();
  context.arc(center.x, center.y, radius, 0, Math.PI * 2);
  context.stroke();
  context.restore();
}

export function renderRadiusHandler(context, area, isActive) {
  const center = area.center;
  const size = Math.min(area.w, area.h);
  drawBlueDot(context, center, size, isActive);
}

// Compute unit vectors along the two edges meeting at a corner
function cornerBasis(region, corner) {
  if (corner === 'topLeft') {
    const e1 = {
      x: region.topRight.x - region.topLeft.x,
      y: region.topRight.y - region.topLeft.y,
    };
    const e2 = {
      x: region.bottomLeft.x - region.topLeft.x,
      y: region.bottomLeft.y - region.topLeft.y,
    };
    return { origin: region.topLeft, u: normalize(e1), v: normalize(e2) };
  }
  if (corner === 'topRight') {
    const e1 = {
      x: region.topLeft.x - region.topRight.x,
      y: region.topLeft.y - region.topRight.y,
    };
    const e2 = {
      x: region.bottomRight.x - region.topRight.x,
      y: region.bottomRight.y - region.topRight.y,
    };
    return { origin: region.topRight, u: normalize(e1), v: normalize(e2) };
  }
  if (corner === 'bottomRight') {
    const e1 = {
      x: region.bottomLeft.x - region.bottomRight.x,
      y: region.bottomLeft.y - region.bottomRight.y,
    };
    const e2 = {
      x: region.topRight.x - region.bottomRight.x,
      y: region.topRight.y - region.bottomRight.y,
    };
    return { origin: region.bottomRight, u: normalize(e1), v: normalize(e2) };
  }
  // bottomLeft
  const e1 = {
    x: region.bottomRight.x - region.bottomLeft.x,
    y: region.bottomRight.y - region.bottomLeft.y,
  };
  const e2 = {
    x: region.topLeft.x - region.bottomLeft.x,
    y: region.topLeft.y - region.bottomLeft.y,
  };
  return { origin: region.bottomLeft, u: normalize(e1), v: normalize(e2) };
}

function normalize(v) {
  const len = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / len, y: v.y / len };
}

function add(a, b, s = 1) {
  return { x: a.x + b.x * s, y: a.y + b.y * s };
}

// Stage-space distance from a corner along both edges (min of two projections)
function distanceAlongCorner(point, basis) {
  const rel = { x: point.x - basis.origin.x, y: point.y - basis.origin.y };
  const du = rel.x * basis.u.x + rel.y * basis.u.y;
  const dv = rel.x * basis.v.x + rel.y * basis.v.y;
  return Math.max(0, Math.min(du, dv));
}

export function isRadiusableNode(node) {
  return (
    node &&
    (node.type === TYPES.image ||
      (node.type === TYPES.shape && (node.shape || SHAPES.rect) === SHAPES.rect) ||
      (node.type === TYPES.text && (node.background || node.borderWidth)))
  );
}

export function getInteractiveRadiusHandlers(stageContext, node, region, scale = 1, unitScale = 1) {
  if (!isRadiusableNode(node)) return [];
  const { interactive } = stageContext.providers;
  if (!hitTestNode(stageContext, interactive, node)) return [];

  // Use the provided region's rect so coordinates match the region space (stage or screen)
  const { w, h } = region && region.rect ? region.rect : stageContext.api.getNodeRegion(node).rect;

  if (w / scale < 96 || h / scale < 96) {
    return [];
  }

  // Convert stored radius to current region units:
  // - numbers/px strings scale by unitScale (stage->screen for render; 1 for hit-test stage)
  // - percentages keep as-is and resolved against current w/h
  const rawRadius = node.radius || 0;
  const radiusInRegionUnits =
    typeof rawRadius === 'string'
      ? rawRadius.endsWith('%')
        ? rawRadius
        : parseFloat(rawRadius) * unitScale
      : rawRadius * unitScale;

  const r = resolveRadius(w, h, radiusInRegionUnits);

  const corners = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
  const minInsetScreen = 28; // desired minimum on-screen inset in px
  const visualSize = 14 * scale; // keep constant visual size on screen

  const handlers = corners.map((corner) => {
    const basis = cornerBasis(region, corner);
    // corner radius in current region units
    const cornerRadius = r[corner] || 0;
    // ensure a minimum on-screen inset; if region is stage-space, scale converts to stage units
    const displayOffset = Math.max(cornerRadius, minInsetScreen * scale);
    const center = add(add(basis.origin, basis.u, displayOffset), basis.v, displayOffset);
    const rect = new Rect(
      center.x - visualSize / 2,
      center.y - visualSize / 2,
      visualSize,
      visualSize,
    );
    rect.id = `radius-${corner}`;
    rect.type = 'radius';
    rect.corner = corner;
    rect.nodeId = node.id;
    return rect;
  });

  return handlers;
}

export function radiusAction(hitTestContext) {
  const { type, targetHandler, stageContext, point, provider } = hitTestContext;
  const interactive = stageContext.providers?.interactive || stageContext.interactive;

  // Hover feedback over radius handler
  if (
    targetHandler &&
    targetHandler.type === 'radius' &&
    type === EventTypes.move &&
    !interactive.movingHandler
  ) {
    interactive.cursor.set('pointer');
    interactive.hoverHandlerId = targetHandler.id;
    stageContext.api.markNeedRender();
    return true;
  }

  // Start dragging radius
  if (type === EventTypes.down && targetHandler && targetHandler.type === 'radius') {
    interactive.movingHandler = targetHandler;
    interactive.hoverHandlerId = targetHandler.id;
    stageContext.api.markNeedRender();
    return true;
  }

  // Dragging to update radius value
  if (
    type === EventTypes.move &&
    interactive.movingHandler &&
    interactive.movingHandler.type === 'radius'
  ) {
    const handler = interactive.movingHandler;
    const node =
      handler.node ||
      (handler.nodeId ? stageContext.api.getNode(handler.nodeId) : null) ||
      hitTestContext.targetNode ||
      hitTestContext.shallowNode;
    if (!node) return true;

    const nodeRegion = stageContext.api.getNodeRegion(node);
    const basis = cornerBasis(nodeRegion, handler.corner);
    const dist = distanceAlongCorner(point, basis);

    // limit by node size (follow resolveRadius logic: if multiple corners, clamp to half of min(w,h))
    const rect = stageContext.api.getNodeRegion(node).rect;
    const maxRadius = Math.min(rect.w, rect.h) / 2;
    const next = Math.max(0, Math.min(dist, maxRadius));

    // Update node.radius as a number (uniform corners)
    stageContext.api.updateNode(node.id, { radius: Math.round(next) });
    stageContext.hooks.onNodeMoveCallback();
    stageContext.api.markNeedRender();
    return true;
  }

  // Mouse up: finish
  if (
    type === EventTypes.up &&
    interactive.movingHandler &&
    interactive.movingHandler.type === 'radius'
  ) {
    if (provider) provider.skipSelectFromUp = true;
    interactive.movingHandler = null;
    stageContext.api.markNeedRender();
    return true;
  }

  return false;
}
